import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  Grid,
  IconButton,
  Typography,
  FormControl,
  MenuItem,
  Select,
  CircularProgress 
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import './dashboard_style.scss';
import shopIcon from '../../../assets/delivery.png';
import mapsIcon  from '../../../assets/ic_tabMaps.png';
import offersIcon  from '../../../assets/ic_tabOrders.png';
import routesIcon  from '../../../assets/ic_tabRoutes.png';
import documentsIcon  from '../../../assets/ic_tabDocuments.png';
import tripsIcon  from '../../../assets/ic_tabTrips.png';
import parentsIcon from '../../../assets/ic_tabParents.png'; 
import stopsIcon from '../../../assets/ic_tabStops.png'; 
import paymentsIcon from '../../../assets/ic_tabPayments.png'; 
import { WEB_VERSION} from '../../../constants/version.ts';
import buildingBackground  from '../../../assets/image.png';
import anytrakkLogo  from '../../../assets/anytrakklogo.png';
import CameraDialog from './camera-modal.tsx';
import PhotoConfirmationModal from './photo-confirmation-modal.tsx';
import VehicleUpdateModal from './vehicle-update-modal.tsx';
import OTPVerifyModal from "./otp-verify-modal.tsx";
import SettingsScreen from "./settings.tsx";
import {Settings as SettingsIcon} from "@mui/icons-material";
import {getDashboard, startEndTripApi, vehicleUserMapping, verifyOTP, 
   editVehicle, editTrips, getOrdersDelivered, editUser, 
  getRoleFeatures, getVehicle, getVehicleRoute
} from './action.ts';
import {getOrders} from '../Orders/action.ts';
import TripForm from './trip-form.tsx';
import formatDate  from '../../Utils/DateFormat/date-format.tsx';
import {getTripButtonState} from '../../Utils/TripButtonType/get-trip-button-state.tsx';
import TripConfirmationModal from './trip-confirmation-modal.tsx';
import {formatVehicleNo}  from '../../Utils/VehicleNumberValidation/vehicle-number-validation.tsx';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConfirmationPopup from '../../../WebApp/Components/ConfirmationModal/confirmation-modal.tsx';

const mockData = {
  labels: {
    tripStatus: "Trip Status",
    startTime: "Start Time",
    orders: "Deliveries",
    trips: "Trip",
    map: "Maps",
    routes: "Routes",
    offers: "Shop",
    documents: "Documents",
    parents: "Parents",
    stops: "Stops",
    payments: "Payments"
  },
  buttons: {
    startTrip: "Start Trip",
    endTrip: "End Trip",
    noCancel: "No, Cancel",
    handover: "Handover"
  },
  alerts: {
    photoConfirmation: "Photo Confirmation",
    confirmAlert: "Confirm",
    endTripConfirm: "Are you sure you want to end the trip?"
  }
};
const ROLE_CONFIG = {
  driver: {
    showEarningsBand: false,
    showTimeSection: true,
    showTripSection: false,
    allowedTabs: ['documents','routes','payments', 'documents', 'offers'],
    tabStyle: 'dark'
  },
  stms_driver: {
    showEarningsBand: false,
    showTimeSection: true,
    showTripSection: false,
    allowedTabs: ['routes', 'parents', 'stops', 'documents'],
    tabStyle: 'dark'
  },
  cargo99_driver: {
    showEarningsBand: true,
    showTimeSection: true,
    showTripSection: true,
    allowedTabs: ['orders', 'trips', 'map', 'routes', 'documents', 'offers'],
    tabStyle: 'light'
  }
};

interface Vehicle {
  id: string;
  number_plate: string;
  make_model?: string;
  vehicles_owners_id?: string;
  fuel_type?: string;
  vehicle_status?: string;
}

interface CurrentTrip {
  id: string | null;
  odometer_start: string;
  orders_picked: string;
  errors: string | null;
  start_time: string | null;
  end_time: string | null;
  status?: string;
  initiated_by?:string;
}

interface LocationData {
  latitude: number;
  longitude: number;
  timestamp: string;
}

const VehicleSelector = React.memo(({ 
  vehicles, 
  selectedVehicle, 
  onVehicleChange 
}: {
  vehicles: Vehicle[];
  selectedVehicle: Vehicle | null;
  onVehicleChange: (vehicle: Vehicle) => void;
}) => {
  if (vehicles.length === 1) {
    return (
      <Box sx={{ 
        minWidth: 120, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        border: '1px solid rgba(0, 0, 0, 0.23)', 
        borderRadius: '4px', 
        height: '40px',
        px: 2 
      }}>
        <Typography variant="body1">
          {vehicles[0].number_plate}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 120 }}>
      <FormControl fullWidth size="small">
        <Select
          value={selectedVehicle?.id || ''}
          onChange={(e) => {
            const selected = vehicles.find((v: Vehicle) => v.id.toString() === e.target.value.toString());
            if (selected) {
              onVehicleChange(selected);
            }
          }}
          displayEmpty
          inputProps={{ 'aria-label': 'Without label' }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
              },
            },
          }}
        >
          <MenuItem value="" disabled>
            Select
          </MenuItem>
          
          {vehicles.map((vehicle: Vehicle) => (
            <MenuItem 
              key={vehicle.id} 
              value={vehicle.id}
              sx={{ whiteSpace: 'normal' }}
            >
              {vehicle.number_plate}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
});

export default function DashboardScreen() {
  const [urlParams, setUrlParams] = useState
  ({
    token: '',
    userId: '',
    userName:'',
    userRole:'',
    userPhone:'',
    appVersion:''
  });
const [uiRole, setUiRole] = useState(() => localStorage.getItem('uiRole') || 'driver');
const [roleFeatures, setRoleFeatures] = useState<string[]>(() => {
  const cached = localStorage.getItem('roleFeatures');
  return cached ? JSON.parse(cached) : [];
});
  const activeRoleConfig = ROLE_CONFIG[uiRole as keyof typeof ROLE_CONFIG] || ROLE_CONFIG.driver;
  const [showPhotoSkipModal, setShowPhotoSkipModal] = useState(false);
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [isStartTrip, setIsStartTrip] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [currentTrip, setCurrentTrip] = useState<CurrentTrip>({
    id: null,
    odometer_start: '',
    orders_picked: '',
    errors: null,
    start_time: null,
    end_time: null,
    initiated_by:''
  });  
  const [tripId, setTripId] = useState(null); 
  const [vehicle, setVehicle] =  useState<Vehicle[]>([]);
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [openCameraDialog, setOpenCameraDialog] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [odometerReading, setOdometerReading] = useState('');
  const [vehicleError, setVehicleError] = useState('');
  const [odometerStart, setOdometerStart] = useState('');
  const [odometerEnd, setOdometerEnd] = useState('');
  const [ordersPicked, setOrdersPicked] = useState('');
  const [ordersDelivered, setOrdersDelivered] = useState('');
  const [totalOrdersDelivered, setTotalOrdersDelivered] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [actionType, setActionType] = useState('');
  const [showTripForm, setShowTripForm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const [pullDown, setPullDown] = useState(0);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [handoverStatus, setHandoverStatus] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showVehicleConfirm, setShowVehicleConfirm] = useState(false);
  const [pendingVehicle, setPendingVehicle] = useState<Vehicle | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [otpVerified, setOtpVerified] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [odometerPic, setOdometerPic] = useState<File | null>(null);
  const [isTripDataFetched, setIsTripDataFetched] = useState(false);
  const [currentAddess, setCurrentAddress]=useState('');
  const [locationError, setLocationError] = useState('');
  const [companiesId, setCompaniesId] = useState('');
  const [totalKm, setTotalKm] = useState<string>('0');
  const [totalDuration, setTotalDuration] = useState<string>('0m');
  const queryParams = new URLSearchParams(window.location.search);
   const [currentLocation, setCurrentLocation] = useState<{
    latitude: number | null;
    longitude: number | null;
    address?: string;
  }>({
    latitude: null,
    longitude: null,
    address: ''
  });
  const [tripConfirmationModal, setTripConfirmationModal] = useState<{
  open: boolean;
  type: 'start' | 'end';
  companyName?: string;
  ofdCount?: string;
}>({
  open: false,
  type: 'start'
});
  const navigate = useNavigate();
  const isCargo99 = uiRole === 'cargo99_driver';

  const isLocationAvailable = currentLocation.latitude !== null && currentLocation.longitude !== null && !locationError;
  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.scrollY <= 0) {
      setStartY(e.touches[0].pageY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (window.scrollY <= 0 && startY > 0) {
      const y = e.touches[0].pageY;
      const diff = y - startY;
      if (diff > 0) {
        setPullDown(Math.min(diff, 100));
      }
    }
  };
const handleConfirmVehicleChange = () => {
  if (pendingVehicle) {
    setSelectedVehicle(pendingVehicle); 
    setVehicleId(pendingVehicle.id);
    
    editVehicle(
      pendingVehicle.make_model || '',  
      pendingVehicle.number_plate || '', 
      urlParams.userId || '', 
      pendingVehicle.vehicles_owners_id || '', 
      pendingVehicle.fuel_type || '', 
      '', 
      '', 
      (response) => toast.success(response.message), 
      (error) => toast.error(error.message)
    );
  }
  
  setShowVehicleConfirm(false);
  setPendingVehicle(null);
};
 const checkVersion = () => {
      const storedVersion = localStorage.getItem('webVersion');
      if (!storedVersion) {
        localStorage.setItem('webVersion', WEB_VERSION);
        return;
      }
      if (storedVersion !== WEB_VERSION) {
        localStorage.setItem('webVersion', WEB_VERSION);
        setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("selectedVehicle");
          localStorage.removeItem("uiRole");
          localStorage.removeItem("roleFeatures");
          if ((window as any).ReactNativeWebView){
            (window as any).ReactNativeWebView.postMessage('logout');
          } 
        }, 1000);
      }
       refreshDashboard();
    };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
 
}, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

useEffect(() => {
  if (vehicleId !== null) {
    localStorage.setItem('vehicle_id', vehicleId);
  }
  if (currentAddess !== null) {
    localStorage.setItem('current_location', currentAddess);
  }
  if ((window as any).ReactNativeWebView) {
    const payload = {
      vehicle_id: vehicleId || '',
      current_location: currentAddess || ''
    };
    (window as any).ReactNativeWebView.postMessage(JSON.stringify(payload));
  }
}, [vehicleId, currentAddess]);

useEffect(() => {
  if (companiesId && (companiesId === '10' || companiesId === '81')) {
    getVehicle(
      100,
      0,
      (response: any) => {
        console.log('Vehicle list fetched with companiesId:', companiesId, response);
        const fetchedVehicles = response?.message?.vehicles_list || response?.message || response?.data || [];
        if (Array.isArray(fetchedVehicles) && fetchedVehicles.length > 0) {
          setVehicles(fetchedVehicles);
        }
      },
      (errMessage: any) => {
        toast.error(errMessage.message);
      },
      { active: 1 },
      '', 
      '', 
      '',
      '',
      companiesId
    );
  }
}, [companiesId, uiRole]);

  useEffect(() => {
    window.isReactAppReady = true;
    window.dispatchEvent(new CustomEvent('reactAppReady'));
    
    const handleLocationUpdate = (event: any) => {
      const locationData = event.detail;
       if (locationData && locationData.type === 'LOCATION_UPDATE' && locationData.data) {
        const { latitude, longitude } = locationData.data;
        setCurrentLocation({ latitude, longitude });
      }
    };

    window.addEventListener('locationUpdate', handleLocationUpdate);
    return () => window.removeEventListener('locationUpdate', handleLocationUpdate);
  }, []);

  const calculateElapsedTime = (startTime: string) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - start.getTime()) / 1000);
    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const processCurrentTrip = (currentTripData:any, vehicleData:any) => {
    if (!currentTripData) return { errors: 'Trip not found' };
    const primaryVehicle = vehicleData.find((v: { vehicle_status: string; }) => v?.vehicle_status === 'primary');
    if (!primaryVehicle) return { errors: 'No primary vehicle found' };
    const vehicleTrip = currentTripData[primaryVehicle?.id];
    return vehicleTrip || { errors: 'Trip not found' };
  };

  const handleTouchEnd = () => {
    if (pullDown > 60) {
      setRefreshing(true);
      refreshDashboard();
    }
    setStartY(0);
    setPullDown(0);
  };

  const refreshDashboard = () => {
    setIsTripDataFetched(false);
    
    getRoleFeatures(
      (response) => {
        const features = response?.message?.features || {};
        const featuresArray = [
          ...(features.enabled || [])
        ].map((f: string) => f.toLowerCase());

        setRoleFeatures(featuresArray);
        localStorage.setItem('roleFeatures', JSON.stringify(featuresArray));
        const company = response?.message?.company;
        const companyId = company?.id?.toString();
        const companyName = company?.name?.toLowerCase();       
       let determinedRole = 'driver';
        
        if (companyId === '95' || companyId === '11' || companyName === 'stms') {
          determinedRole = 'stms_driver';
        } else if (featuresArray.includes('trips')) {
          determinedRole = 'cargo99_driver';
        }
        
        setUiRole(determinedRole);
        
        localStorage.setItem('uiRole', determinedRole);
        if (featuresArray.includes('dashboard_orders_delivered')) {
          getOrdersDelivered((resp)=>{
            setTotalOrdersDelivered(resp.message.total_orders_delivered)
          }, (error)=>{toast.error(error.message)})
        }

        getDashboard(
          (dashboardResp) => {                    
            if (dashboardResp.status === 200) {
              setVehicles(dashboardResp.message.vehicles);
              const primaryVehicle = dashboardResp.message.vehicles.find((v: { vehicle_status: string; }) => v.vehicle_status === 'primary');
              setSelectedVehicle(primaryVehicle);
              console.log('Primary vehicle found:', primaryVehicle);
              setVehicleId(primaryVehicle?.id);
              setCompaniesId(primaryVehicle?.companies_id);
              const processedTrip = processCurrentTrip(dashboardResp.message.current_trip, dashboardResp.message.vehicles);
              setCurrentTrip(processedTrip);
              setTripId(processedTrip.id);
              if(processedTrip.errors === 'Trip not found' || processedTrip.start_time === null){
                setActionType('start');
              } else {
                setActionType('end');
              }
              
              if (featuresArray.includes('orders')) {
                getOrders(
                  50, 0, '', '', '', processedTrip.id || '' , '', '', '', '', primaryVehicle?.id, '', 1,
                  (orderResp) => {
                    setHandoverStatus(orderResp?.message?.total_count > 0);
                    setIsTripDataFetched(true); 
                  },
                  (error) => {
                    setHandoverStatus(false);
                    setIsTripDataFetched(true);
                  }
                );
              } else {
                setHandoverStatus(false);
                setIsTripDataFetched(true);
              }

            } else {
              toast.error(dashboardResp.message);
              setRefreshing(false);
              setIsTripDataFetched(true);
            }
            setRefreshing(false);
          },
          (errorMessage) => {
            toast.error(errorMessage.message);
            setRefreshing(false);
            setIsTripDataFetched(true);
          }
        );
      },
      (error) => {
        setRefreshing(false);
        setIsTripDataFetched(true);
      }
    );
  };

  useEffect(() => {
    const userPhoneFromQuery = queryParams.get('user_phone');
    const userPhone = userPhoneFromQuery || localStorage.getItem("user_phone") || '';
    if (userPhone) localStorage.setItem("user_phone", userPhone);
    
    const initialRole = queryParams.get('user_role') || 'driver';

    setUrlParams({
      token: queryParams.get('token') || '',
      userId: queryParams.get('user_id') || '',
      userName: queryParams.get('user_name') || '',
      userRole: initialRole,
      userPhone: userPhone,
      appVersion:queryParams.get('app_version') ||''
    });
    
    refreshDashboard();
  }, []);

  useEffect(() => {
    if (vehicleId && (uiRole === 'stms_driver' || uiRole === 'driver')) {
      const today = new Date();
      const dateString = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
      const from_date = `${dateString} 00:00:00`;
      const to_date = `${dateString} 23:59:59`;

      getVehicleRoute(
        vehicleId,
        from_date,
        to_date,
        (response) => {
          const km = response?.data?.distance_km ?? 0;
          const totalMinutes = response?.data?.time_taken?.minutes ?? 0;
          const totalHours = response?.data?.time_taken?.hours ?? 0;
          let formattedDuration = '0m';
          if (totalHours >= 1) {
            const remainingMins = Math.round(totalMinutes % 60);
            formattedDuration = `${Math.floor(totalHours)}h ${remainingMins}m`;
          } else if (totalMinutes > 0) {
            formattedDuration = `${Math.round(totalMinutes)}m`;
          }

          setTotalKm(`${km} km`);
          setTotalDuration(formattedDuration);
        },
        (error) => {
          console.error("Failed to fetch vehicle route stats", error);
        }
      );
    }
  }, [vehicleId, uiRole, refreshing]);
  const checkOrdersStatus = () => {
    if (roleFeatures.includes('orders')) {
    
    if (actionType === 'end') {
      getOrders(
        50, 0, 'OFD', '', '', tripId, '', '', '', '', vehicleId, '', 1,
        (response) => {
          if (response?.message?.total_count > 0) {
            setTripConfirmationModal({
              open: true,
              type: 'end',
              ofdCount: response.message.total_count.toString()
            });
          } else {
            proceedWithTripStart();
          }
        },
        (error) => {
          toast.error("Error checking orders: " + error);
          proceedWithTripStart();
        }
      );
    } else {
      getOrders(
        50, 0, 'vehicle assigned', '', '', tripId, '', '', '', '', vehicleId, '', 1,
        (response) => {
          if (response?.message?.total_count > 0) {
            setTripConfirmationModal({
              open: true,
              type: 'start',
              companyName: response.message.orders_list[0].companies_name
            });
          } else {
            proceedWithTripStart();
          }
        },
        (error) => {
          toast.error("Error checking orders: " + error);
          proceedWithTripStart();
        }
      );
    }
  }
  else {
     proceedWithTripStart();
  }
  };
  useEffect(() => {
    const is2WEV = selectedVehicle?.fuel_type === '2W-EV';
    if (tripId !== null) {
      if (is2WEV) {
        if (currentTrip?.orders_picked  && currentTrip?.initiated_by !== 'driver' && currentTrip.orders_picked.toString().trim() !== '') {
          setOrdersPicked(currentTrip.orders_picked.toString());
        } else {
          setOrdersPicked('');
        }
      } else {
        if (actionType === "start" && currentTrip?.orders_picked && currentTrip?.initiated_by !== 'driver') {
          setOrdersPicked(currentTrip.orders_picked.toString());
        } else {
          setOrdersPicked('');
        }
      }
    } else {
      setOrdersPicked('');
    }
  }, [tripId, actionType, currentTrip, selectedVehicle]);

  const proceedWithTripStart = () => {
    setShowTripForm(true);
    setTripConfirmationModal({ open: false, type: 'start' });
  };

  const handleTripConfirmation = () => {
    setShowTripForm(true);
    setTripConfirmationModal({ open: false, type: tripConfirmationModal.type });
  };

  const handleTripCancel = () => {
    setTripConfirmationModal({ open: false, type: tripConfirmationModal.type });
    if(actionType=== 'start') {
      setTripId(null);
      proceedWithTripStart();
    }
  };
  
  const handleTripClose = () => {
    setTripConfirmationModal({ open: false, type: tripConfirmationModal.type });
  };

  const handleHandoverClick = () => {
    editTrips(tripId,'',ordersDelivered,ordersPicked,odometerEnd,'handover',
      (response) => {  
        toast.success(response.message);
        setOdometerReading(''); setOrdersPicked(''); setOrdersDelivered('');
        setShowTripForm(false); setOdometerStart(''); setOdometerEnd(''); setOdometerPic(null);
        refreshDashboard();
      },
      (error) => {toast.error(error.message)}
    )
  };

  useEffect(() => {
    if (!queryParams.get('app_version')) return;
    const storedVersion = localStorage.getItem('appVersion');
    if (storedVersion !== queryParams.get('app_version')) {
      editUser(queryParams.get('app_version'), (response)=>{}, (error) => {});
      localStorage.setItem('appVersion', queryParams.get('app_version') || '');
    }
  }, []);

  const allMenuTabs = [
    { title: mockData.labels.orders, key: "orders", icon: shopIcon, url: `/wv/orders?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&current_location=${currentLocation.address}&vehicle_number=${selectedVehicle?.number_plate}` },
    { title: mockData.labels.trips, key: "trips", icon: tripsIcon, url: `/wv/trips?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&current_location=${currentLocation.address}&vehicle_number=${selectedVehicle}` },
      { title: mockData.labels.payments, key: "payments", icon: paymentsIcon, url: "#" } ,
    { title: mockData.labels.map, key: "map", icon: mapsIcon, url: "geo:0,0?q=" },
    { title: mockData.labels.routes, key: "routes", icon: routesIcon, url: `/wv/routes?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&current_location=${currentLocation.address}&companies_id=${companiesId}&vehicle_number=${selectedVehicle?.number_plate}` },
    { title: mockData.labels.offers, key: "offers", icon: offersIcon, url: `/wv/offers?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&user_role=${urlParams.userRole}&user_phone=${urlParams.userPhone}&app_version=${urlParams.appVersion}&current_location=${currentLocation.address}` },
    { title: mockData.labels.documents, key: "documents", icon: documentsIcon, url:  `/wv/documents?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&current_location=${currentLocation.address}&vehicle_number=${selectedVehicle?.number_plate}` },
    { title: mockData.labels.parents, key: "parents", icon: parentsIcon, url: `/wv/parents?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&vehicle_number=${selectedVehicle?.number_plate}` },
    { title: mockData.labels.stops, key: "stops", icon: stopsIcon, url: `/wv/stops?vehicle_id=${vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}&user_name=${urlParams.userName}&vehicle_number=${selectedVehicle?.number_plate}&companies_id=${companiesId}` },
  
  ];

  const visibleTabs = allMenuTabs.filter(tab => activeRoleConfig.allowedTabs.includes(tab.key));

  const getTabStyles = () => {
    if (activeRoleConfig.tabStyle === 'dark') {
      return {
        background: 'linear-gradient(135deg, #ea1f25 20%, #000000 100%)', 
        color: '#ffffff',
        borderColor: 'transparent'
      };
    }
    return {
      background: 'linear-gradient(1turn, rgb(211, 211, 211), rgb(255, 255, 255))', 
      color: 'inherit',
      borderColor: 'rgb(204, 204, 204)'
    };
  };

  const handleNavigation = (url: string, key:any) => {
    if (key === 'orders' || key === 'offers'|| key === 'payments') {
      return; 
    }
   if(key === 'map') window.open(`https://www.google.com/maps?q=`, '_blank');
   else navigate(url);
  };

  const startEndTrip = async() => {
    if (currentTrip.start_time === null || currentTrip.errors === 'Trip not found'){
      setIsStartTrip(true); 
      setTripId(currentTrip?.id)
    } else {
      setTripId(currentTrip.id);
      setIsStartTrip(false); 
    }
    
    if (parseInt(odometerEnd) < parseInt(currentTrip.odometer_start)) {
      toast.error("End Odometer Reading must be greater than Start Odometer Reading.");
      return;
    }
    
    if (parseInt(currentTrip?.orders_picked || ordersPicked) < parseInt(ordersDelivered)) {
      toast.error("Order Delivered should be less than order picked up");
      return;
    }
    
    const is2WEV = selectedVehicle?.fuel_type === '2W-EV';
    const ordersToSend = { start: is2WEV && actionType === 'start' ? '' : ordersPicked, end: ordersDelivered };
     
    startEndTripApi(currentLocation.latitude, currentLocation.longitude, tripId, vehicleId, actionType === 'end' ? 1 :0, 
      (response) => {
        if(response.data.status === 400) {
          toast.error(response.data.message.errors.latitude || response.data.message.errors.longitude ? "Please enable your Location Services" : "Please contact your supervisor");
        } else if (response.data.status === 200 || response.data.status === 201) {
          if(response.data.message.trip_id !== null) { 
            toast.success(response.data.message.message);
            setOdometerReading(''); setOrdersPicked(''); setOrdersDelivered('');
            setShowTripForm(false); setOdometerStart(''); setOdometerEnd(''); setOdometerPic(null);
            refreshDashboard();
          }
        }
      },
      (errorMessage) => toast.error(errorMessage.message), actionType, odometerPic, odometerStart, odometerEnd, ordersToSend.start, ordersToSend.end
    )
  };

  const handleOdometerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^[0-9]*$/.test(value)) setOdometerReading(value);
    actionType === 'start' ? setOdometerStart(value) : setOdometerEnd(value);
  };

  const handleOrdersChange = (action: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^[0-9]*$/.test(value)) {    
      action === 'start' ? setOrdersPicked(value) : setOrdersDelivered(value);
    }
  };

  const handleVerifyOTP = (otp: string): Promise<{ success: boolean; message?: string }> => {
    return new Promise((resolve, reject) => {
      verifyOTP(urlParams.userPhone, '91', otp,
        (response) => {
          if (response.data.status === 200) {
            toast.success(response.data.message); setOtpVerified('1'); resolve({ success: true });
            handleVehicleUpdate();
          } else resolve({ success: false, message: response.data.message|| 'OTP verification failed.' });
        },
        (errorMessage) => resolve({ success: false, message: errorMessage })
      );
    });
  };

  const handleVehicleUpdate = () => {
    if (vehicleError) return toast.error("Fix the errors");
    if (!vehicleNumber) return toast.error("Vehicle number cannot be empty");
    
    vehicleUserMapping(urlParams.userId, vehicleNumber, otpVerified,
      (response) => {
        if (response.status === 200) {
          if (response?.data?.status === 308) toast.error(response.data.message);
          else if (response?.data?.status === 400) toast.error("Please contact your administrator");
          else if (['OTP sent.', 'Old OTP expired. New OTP generated and re-sent.', 'OTP re-sent.'].includes(response?.data?.message)) setShowOTPModal(true);
          else if (response.data.message.message === 'Driver updated successfully') {
            toast.success(response?.data?.message?.message); setVehicleDialog(false);
          } else toast.error(response?.message || 'An error occurred');
        } else if (response.status === 201) {
          setVehicleId(response.id); toast.success(response?.data?.message?.message);
        }
      },
      (error) => toast.error(error.message || "Error updating vehicle information")
    );
  };

  const handleVehicleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedResult = formatVehicleNo(e.target.value);
    setVehicleNumber(formattedResult.formatted); setVehicleError(formattedResult.error);
  };

  useEffect(() => {
    if ((window as any).ReactNativeWebView) (window as any).ReactNativeWebView.postMessage('REQUEST_LOCATION');
  }, []);

  const handleTripButtonClick = async (skipConfirmation: boolean = false) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'START_TRIP' }));
      const handleLocationResponse = (event: any) => {
        const locationData = event.detail || event;
        if (locationData.type === 'LOCATION_ERROR') return window.removeEventListener('locationUpdate', handleLocationResponse);
        if (locationData.type === 'LOCATION_UPDATE' && locationData.data) {
          const { latitude, longitude } = locationData.data;
          setCurrentLocation({ latitude, longitude });
          
          if (!selectedVehicle) toast.error("Vehicle is not mapped. Please Contact your Supervisor");
          else {
            if (skipConfirmation) proceedWithTripStart();
            else {
              if (currentTrip.status === 'trip started' && handoverStatus && roleFeatures.includes('orders')) {
                getOrders(
                  50, 0, 'delivered', '', '', tripId, '', '', '', '', vehicleId, '', 1,
                  (response) => {
                    if (response?.message?.total_count > 0) {
                      setOrdersDelivered(response.message.total_count.toString());
                    } else {
                      setOrdersDelivered('0');
                    }
                  },
                  (error) => {
                    toast.error("Error fetching delivered orders: " + error);
                    setOrdersDelivered('');
                  }
                );
              }
              checkOrdersStatus();
            }
          }
        }
        
        window.removeEventListener('locationUpdate', handleLocationResponse);
      };
      window.addEventListener('locationUpdate', handleLocationResponse);
      setTimeout(() => window.removeEventListener('locationUpdate', handleLocationResponse), 10000);
      
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
          if (!selectedVehicle) toast.error("Vehicle is not mapped. Please Contact your Supervisor");
          else {
            if (skipConfirmation) {
              proceedWithTripStart();
            }
            else checkOrdersStatus();
          }
        },
        () => toast.error('Location access is required to start trip')
      );
    }
  };

  const cancelTripAction = () => {
    setShowTripForm(false); setOdometerReading(''); setOrdersPicked(''); setOrdersDelivered('');
  }

  useEffect(() => {
    if (odometerPic) {
      const hasStartTime = currentTrip && currentTrip.start_time;
      const hasEndTime = currentTrip && currentTrip.end_time;
      const isHandoverStatus = currentTrip.status === 'handover';
      if (hasStartTime && !hasEndTime && !isHandoverStatus && handoverStatus) handleHandoverClick();
      else startEndTrip();
    }
  }, [odometerPic]);

  const getTripButton = () => {
    if (!isTripDataFetched || !selectedVehicle) return <Button variant="contained" fullWidth disabled sx={{ backgroundColor: '#E0E0E0' }}>Loading...</Button>;
    const buttonState = getTripButtonState(currentTrip, handoverStatus);

    return (
      <Button
        variant="contained" fullWidth disabled={buttonState.disabled}
        sx={{
          backgroundColor: buttonState.backgroundColor,
          fontSize: '1.1rem', lineHeight: '1.2',
          '&:hover': { backgroundColor: buttonState.disabled ? buttonState.backgroundColor : buttonState.type === 'start' ? '#1CA825' : buttonState.type === 'end' ? '#E63939' : buttonState.type === 'handover' ? '#C67A14' : buttonState.backgroundColor, },
          '&.Mui-disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E', },
        }}
        onClick={buttonState.disabled ? undefined : () => handleTripButtonClick(false)}
      >
        {buttonState.label}
      </Button>
    );
  };

  return (
    <>
    <Box 
      ref={containerRef}
      sx={{ 
        paddingTop:'0px', 
        display:'flex', 
        flexDirection:'column', 
        alignItems:'center', 
        justifyContent:'center',
        '&::before': { 
            content: '""', 
            position: 'absolute', 
            top: 0, left: 0, right: 0, bottom: 0, 
            backgroundImage: `url(${buildingBackground})`, 
             backgroundSize: 'cover', 
            backgroundPosition: 'center', 
            backgroundRepeat: 'no-repeat', 
            paddingTop:'30px', 
            zIndex: 0, 
            marginBottom:'0px', 
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '0%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '250px',
            height: '80px',
            backgroundImage: `url(${anytrakkLogo})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0, 
        },
        '& > *': { position: 'relative', zIndex: 1 }
      }}
      onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
    >
      {refreshing && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px', zIndex: 1000, backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
          <Typography>Refreshing...</Typography>
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px', backgroundColor: 'white', boxShadow: 1, width: '100%', marginTop:'35px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" sx={{ mr: 1 }}>👋</Typography>
          <Typography sx={{ fontSize: '20px', mr: 2 }}>{urlParams.userName}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VehicleSelector 
  vehicles={vehicles} 
  selectedVehicle={selectedVehicle}
  onVehicleChange={(selected) => {
    setPendingVehicle(selected);
    setShowVehicleConfirm(true);
  }}
/>
          <IconButton onClick={() => setOpenSettings(true)}><SettingsIcon /></IconButton>
        </Box>
      </Box>
    
      {activeRoleConfig.showEarningsBand && (
        <Card
          sx={{
            marginBottom: '10px', marginTop: '10px', width: '350px', overflow: 'hidden', borderRadius: '24px',
            background: 'linear-gradient(135deg,#ea1f25 20%,#000)!important', backdropFilter: 'blur(12px)',
            transition: 'transform 0.35s ease, box-shadow 0.35s ease', position: 'relative',
            '&:hover': { transform: 'translateY(-4px) scale(1.015)', boxShadow: `0 10px 28px rgba(0,0,0,0.18), inset -4px -4px 10px rgba(255,255,255,0.6), inset 4px 4px 10px rgba(0,0,0,0.06)` },
            animation: 'bubbleFloat 4s ease-in-out infinite'
          }}
        >
          <Grid container spacing={0} textAlign="center">
            <Grid item xs={6} sx={{ borderRight: '1px solid #e0e0e0', padding: '8px 8px', position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: '0', left: '8px', right: '8px', height: '1px', backgroundColor: '#e0e0e0'} }}>
              <Typography variant="subtitle2" sx={{ color: '#ffffffff', fontWeight: 600, marginBottom: '8px', paddingBottom: '8px', position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: 0, left: '25%', right: '25%', height: '2px', backgroundColor: '#72ff7aff', borderRadius: '1px' }}}>Deliveries</Typography>
              <Typography variant="h6" sx={{ color: '#82ff88ff', fontWeight: 700, fontSize: '1.35rem', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{totalOrdersDelivered}</Typography>
            </Grid>
            <Grid item xs={6} sx={{ borderRight: '1px solid #e0e0e0', padding: '8px 8px', position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: '0', left: '8px', right: '8px', height: '1px', backgroundColor: '#e0e0e0'} }}>
              <Typography variant="subtitle2" sx={{ color: '#ffffffff', fontWeight: 600, marginBottom: '8px', paddingBottom: '8px', position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: 0, left: '25%', right: '25%', height: '2px', backgroundColor: '#ff7676ff', borderRadius: '1px' }}}>Earnings</Typography>
              <Typography variant="h6" sx={{ color: '#ff9d9dff', fontWeight: 700, fontSize: '1.35rem', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}></Typography>
            </Grid>
          </Grid>
        </Card>
      )}

      <Box sx={{ px: 2, marginTop:'5px', marginBottom:'5px'}}>
        
        {activeRoleConfig.showTimeSection && (
          <Box sx={{ marginBottom: uiRole === 'cargo99_driver' ? '12px' : '20px' }}>
            <Card sx={{ mb: activeRoleConfig.showTripSection ? 2 : 0, padding:'10px'}}>
              {isCargo99 ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body1">
                     {(currentTrip.errors === 'Trip not found' || currentTrip.start_time === null) ? 'Time' : mockData.labels.startTime}
                    </Typography>
                    <Typography sx={{color:'#ea1f25 ', fontSize:'20px'}}>
                      {(currentTrip.errors === 'Trip not found' || currentTrip.start_time === null) ? currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : formatDate(currentTrip.start_time, { showFullDate: false })}
                    </Typography>
                  </Box>
                  {currentTrip.errors !== 'Trip not found' && currentTrip.start_time && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">Elapsed:</Typography>
                      <Typography sx={{color:'#ea1f25 ', fontSize:'20px'}}>{calculateElapsedTime(currentTrip.start_time)}</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                (!totalKm || totalKm === '0' || totalKm === '0 km') && (!totalDuration || totalDuration === '0' || totalDuration === '0m') ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">Time:</Typography>
                      <Typography sx={{ color: '#ea1f25', fontSize: '20px', fontWeight: 600 }}>
                        {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">Total KM:</Typography>
                      <Typography sx={{ color: '#ea1f25', fontSize: '20px', fontWeight: 600 }}>
                        {totalKm}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">Duration:</Typography>
                      <Typography sx={{ color: '#ea1f25', fontSize: '20px', fontWeight: 600 }}>
                        {totalDuration}
                      </Typography>
                    </Box>
                  </Box>
                )
              )}
            </Card>
          </Box>
        )}

        {activeRoleConfig.showTripSection && (
          <Box sx={{ marginBottom:'12px' }}>
            {isLocationAvailable ? (
              showTripForm ? (
                <TripForm open={showTripForm} onClose={cancelTripAction} onNext={() => setShowPhotoSkipModal(true)} onCancel={cancelTripAction} actionType={actionType} selectedVehicle={selectedVehicle} odometerReading={odometerReading} onOdometerChange={handleOdometerChange} ordersPicked={ordersPicked} onOrdersPickedChange={handleOrdersChange('start')} ordersDelivered={ordersDelivered} onOrdersDeliveredChange={handleOrdersChange('end')} currentTrip={currentTrip} handoverStatus={handoverStatus}/>
              ) : ( getTripButton() )
            ) : ( getTripButton() )}
          </Box>
        )}
        
        <Box sx={{ position: 'relative' }}>
          {(refreshing || !isTripDataFetched) && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: 0, left: 0, right: 0, bottom: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '8px'
              }}
            >
              <CircularProgress size={48} sx={{ color: '#ea1f25' }} />
            </Box>
          )}

          <Grid container spacing={isCargo99 ? 2 : 5}>
            {visibleTabs.map((item, index) => {
              const tabStyle = getTabStyles();
              const isGenericDisabled = refreshing || !isTripDataFetched;
              const isRouteDisabledDueToVehicle = item.key === 'routes' && (!selectedVehicle || !vehicleId);
              
              const visuallyDisabled = isGenericDisabled || isRouteDisabledDueToVehicle; 

              return (
                <Grid item xs={6} sm={4} md={2} key={index}>
                  <Card 
                    sx={{ 
                        borderColor: tabStyle.borderColor, borderWidth: '1px', borderStyle: 'solid',
                        transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                        background: tabStyle.background, color: tabStyle.color, height: '112px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding:'10px', textAlign: 'center',
                        paddingTop: isCargo99 ? '10px' : '15px',
                        opacity: visuallyDisabled ? 0.6 : 1,
                        pointerEvents: isGenericDisabled ? 'none' : 'auto', 
                        
                        '&:hover': { 
                          boxShadow: visuallyDisabled ? 'none' : 3, 
                          transform: visuallyDisabled ? 'none' : 'translateY(-2px)', 
                          transition: 'all 0.3s ease', 
                          cursor: visuallyDisabled ? (isRouteDisabledDueToVehicle ? 'pointer' : 'not-allowed') : 'pointer'
                        }
                    }}
                    onClick={() => {
                      if (isGenericDisabled) return;
                      
                      if (isRouteDisabledDueToVehicle) {
                        toast.error("Contact your administrator");
                        return;
                      }
                      
                      handleNavigation(item.url, item.key);
                    }}
                  >
                    <Box 
                      component="img" 
                      src={item.icon} 
                      alt={item.title} 
                      sx={{ 
                        width: 60, height: 'auto', mb: 1,
                        filter: visuallyDisabled ? 'grayscale(100%)' : 'none'
                      }} 
                    />
                    <Typography variant="body1" sx={{ color: tabStyle.color }}>{item.title}</Typography>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      </Box>

      <TripConfirmationModal open={tripConfirmationModal.open} onCancel={handleTripCancel} onClose={handleTripClose} onConfirm={handleTripConfirmation} type={tripConfirmationModal.type} companyName={tripConfirmationModal.companyName} ofdCount={tripConfirmationModal.ofdCount}/>
      <PhotoConfirmationModal
        open={showPhotoSkipModal} onClose={() => setShowPhotoSkipModal(false)}
        onSkip={() => {
          const hasStartTime = currentTrip && currentTrip.start_time;
          const hasEndTime = currentTrip && currentTrip.end_time;
          const isHandoverStatus = currentTrip.status === 'handover';
          if (hasStartTime && !hasEndTime && !isHandoverStatus && handoverStatus) handleHandoverClick(); else startEndTrip();
        }}
        onTakePhoto={() => setOpenCameraDialog(true)}
      />
      <CameraDialog open={openCameraDialog} onClose={() => {setOpenCameraDialog(false); setCapturedImage(null);}} onCapture={(imageData) => setOdometerPic(imageData.file)} capturedImage={capturedImage} setCapturedImage={setCapturedImage}/>
<ConfirmationPopup 
  open={showVehicleConfirm}
  onClose={() => {
    setShowVehicleConfirm(false);
    setPendingVehicle(null);
  }}
  onConfirm={handleConfirmVehicleChange}
  title="Confirm Vehicle Change"
  message={`Are you sure you want to switch your vehicle to ${pendingVehicle?.number_plate}?`}
/>
      <VehicleUpdateModal open={vehicleDialog} onClose={() => setVehicleDialog(false)} onSave={handleVehicleUpdate} vehicleNumber={vehicleNumber} onVehicleNumberChange={handleVehicleNumberChange} vehicleError={vehicleError} currentVehicle={vehicle?.number_plate}/>
      <Dialog fullScreen open={openSettings} onClose={() => setOpenSettings(false)}>
        <SettingsScreen onClose={() => setOpenSettings(false)} userRole={urlParams.userRole} appVersion={urlParams.appVersion}/>
        <OTPVerifyModal isOpen={showOTPModal} handleClose={() => setShowOTPModal(false)} phoneNumber={urlParams.userPhone} handleVerifyOTP={handleVerifyOTP}/>
      </Dialog>
    </Box>
    </>
  );
}